import { SerializedData } from '../models/dto/dto.js';
import { Language } from '../models/serializable/language.js';
import { MapUtils } from './map-utils.js';
import { isNotNullOrUndefined, isNullOrUndefined } from './null-safety.js';

export function getAnyFromSerializedData(
    data: SerializedData,
    language?: Language | null,
    validator?: (v: string) => boolean,
): string | null {
  const tryGetValue = (language: Language): string | null => {
    const value = MapUtils.getOrNull<string>(data, language);
    if(isNotNullOrUndefined(value) && (isNullOrUndefined(validator) || validator(value))) {
      return value;
    }

    return null;
  };

  // Try to get string in the requested language.
  if(isNotNullOrUndefined(language)) {
    const value = tryGetValue(language);
    if(isNotNullOrUndefined(value)) {
      return value;
    }
  }

  // If that doesn't work try to get the string in English.
  if(language !== Language.English) {
    const value = tryGetValue(Language.English);
    if(isNotNullOrUndefined(value)) {
      return value;
    }
  }

  // If nothing works return the first available string or null.
  // eslint-disable-next-line keyword-spacing
  for(const language of [Language.English, Language.Hungarian, Language.Romanian]) {
    const value = tryGetValue(language);
    if(isNotNullOrUndefined(value)) {
      return value;
    }
  }

  return null;
}

export function getAnyFromSerializedDataOrDefault(
    data: SerializedData,
    defValue: string,
    language?: Language | null,
    validator?: (v: string) => boolean,
): string {
  return getAnyFromSerializedData(data, language, validator) ?? defValue;
}

export function getAnyFromLocalizationMap<T>(
    map: Map<Language, T>,
    language?: Language | null,
    validator?: (v: T) => boolean,
): T | null {
  // Try to get string in the requested language.
  if(isNotNullOrUndefined(language)) {
    const value = map.get(language);
    if(isNotNullOrUndefined(value) && (isNullOrUndefined(validator) || validator(value))) {
      return value;
    }
  }

  // If that doesn't work try to get the string in English.
  if(language !== Language.English) {
    const value = map.get(Language.English);
    if(isNotNullOrUndefined(value) && (isNullOrUndefined(validator) || validator(value))) {
      return value;
    }
  }

  // If nothing works return the first available string or null.
  if(isNotNullOrUndefined(validator)) {
    for(const value of map.values()) {
      if(validator(value)) {
        return value;
      }
    }

    return null;
  }
  else {
    return map.size > 0 ? (map.values().next().value ?? null) : null;
  }
}

export function getAnyFromLocalizationMapOrDefault<T>(
    map: Map<Language, T>,
    defValue: T,
    language?: Language | null,
    validator?: (v: T) => boolean,
): T {
  return getAnyFromLocalizationMap(map, language, validator) ?? defValue;
}
